import React from "react";

import { TourNavigationEvent } from ".";
import type { ComputedPlacement as TooltipPlacement } from "@popperjs/core";
import { commitMutation, fetchQuery, graphql } from "relay-runtime";

import { TRANSCRIPT_TAB } from "~/constants/meetingRecorderTabs";
import { ToggleNavbarDropdownEvent } from "~/layout/ToggleNavbarDropdownEvent";
import { openRecapModal } from "~/meetings/MeetingModal/openRecapModal";
import { environment } from "~/models";
import { NavPanelDispatchEvent } from "~/nav";
import { __globalModalContext } from "~/ui/modals";

import type { WelcomeChecklistTaskKey } from "~/__generated__/ChecklistCompleteTaskMutation.graphql";
import type { Tours_DemoRecordingQuery as DemoRecordingQuery } from "~/__generated__/Tours_DemoRecordingQuery.graphql";

export type TourStep = {
	/** Use this function to navigate, reveal or
	 * otherwise make available elements you'd like to point out during the tour.
	 */
	prepareApp?: () => Promise<void>;

	tooltipTitle: string;
	tooltipContent: string | React.ReactNode;
	tooltipPlacement: TooltipPlacement;

	/** A selector for the element the tooltip should be attached to.
	 * Convention is to use `data-tour-tooltip-target` to specify a unique identifier.
	 */
	tooltipTarget: string;

	/** The amount of padding to add to the spotlight of the target element. */
	tooltipTargetPadding?: Pick<DOMRect, "top" | "left" | "right" | "bottom">;

	/** Dims everything except the tooltipTarget. Should be true if interactionTarget is set. */
	showSpotlight: boolean;

	/** If false the user can't click within the spotlight. */
	allowInteraction?: boolean;

	/** Specify a selector, and the tour will only move forward if a matching element is clicked.
	 * Convention is to use `data-tour-interaction-target` to specify a unique identifier.
	 */
	interactionTarget?: string;
};

export type Tour = {
	title: string;
	description: string;
	steps: Array<TourStep>;
	helpCentreLink?: string;
	finalStep?: () => void;
};

const WelcomeChecklistTours = {
	FIRST_AGENDA_PREPARED: {
		title: "Prepare your first agenda",
		description: "Set your meeting up for success with a structured and collaborative agenda.",
		steps: [
			{
				tooltipTitle: "Select an upcoming meeting",
				tooltipContent: "Pick an upcoming meeting that you'd like to prepare an agenda for.",
				tooltipPlacement: "right",
				tooltipTarget: "[data-tour-tooltip-target='side-panel']",
				showSpotlight: true,
				interactionTarget: "[data-tour-interaction-target='event-in-panel']",
				allowInteraction: true,
				prepareApp: async () => {
					window.dispatchEvent(new NavPanelDispatchEvent("meetings", true));
				},
			},
			{
				tooltipTitle: "This is your collaborative agenda",
				tooltipContent:
					"Set your meeting up for success with an agenda and talking points all attendees can see and add to.",
				tooltipPlacement: "top",
				tooltipTarget: "[data-tour-tooltip-target='agenda-editor']",
				showSpotlight: true,
				allowInteraction: true,
			},
			{
				tooltipTitle: "Fully collaborative",
				tooltipContent:
					// eslint-disable-next-line max-len
					"Anyone with access to the agenda will appear in this list. Manage access and share with more people from here.",
				tooltipPlacement: "bottom",
				tooltipTarget: "[data-tour-tooltip-target='attendees'] > *:first-child",
				showSpotlight: false,
				allowInteraction: true,
			},
			{
				tooltipTitle: "Share your agenda",
				tooltipContent: "Send your agenda in advance so everyone knows what to expect when the meeting starts.",
				tooltipPlacement: "bottom",
				tooltipTarget: "[data-tour-tooltip-target='share-notes-button']",
				showSpotlight: false,
				allowInteraction: true,
			},
		],
	},
	REVIEW_FIRST_RECAP: {
		title: "Review your first recap",
		description: "Review your meeting recording, transcript, and recap along with action items and highlights.",
		steps: [
			{
				tooltipTitle: "All your recaps in one place",
				tooltipContent:
					"Your AI notes and recaps will all be stored in your library. " +
					"Use channels to organize and share them across your workspace.",
				tooltipPlacement: "right",
				tooltipTarget: "[data-tour-tooltip-target='side-panel']",
				showSpotlight: true,
				allowInteraction: false,
				prepareApp: async () => {
					window.dispatchEvent(new NavPanelDispatchEvent("library", true));
					window.dispatchEvent(new TourNavigationEvent("/library/shared-with-me"));
				},
			},
			{
				tooltipTitle: "Select a Recap to review",
				tooltipContent: "Here's a demo meeting for you to see what a Recap looks like in Fellow - select it now.",
				tooltipPlacement: "left",
				tooltipTarget: "[data-tour-tooltip-target='video-card']",
				tooltipTargetPadding: {
					top: 15,
					left: 15,
					right: 15,
					bottom: 15,
				},
				showSpotlight: true,
				allowInteraction: false,
			},
			{
				tooltipTitle: "This is your AI Recap",
				tooltipContent:
					"Here you can find your recording and AI-generated content including a " +
					"summary, action items, decisions, key points, and so much more.",
				tooltipPlacement: "bottom",
				tooltipTarget: "[data-tour-tooltip-target='recap-tab']",
				showSpotlight: false,
				allowInteraction: false,
				prepareApp: async () => {
					// open the transcript modal for the demo recording
					const data = await fetchQuery<DemoRecordingQuery>(
						environment,
						graphql`
							query Tours_DemoRecordingQuery {
								viewer {
									demoRecording {
										id
										note {
											id
										}
									}
								}
							}
						`,
						{},
					).toPromise();

					if (!data) {
						throw new Error("No data returned from DemoRecordingQuery");
					}

					openRecapModal({
						recordingId: data.viewer.demoRecording.id,
						noteId: data.viewer.demoRecording.note?.id,
					});
				},
			},
			{
				tooltipTitle: "AI-generated Action items",
				tooltipContent: "Any action items detected during the meeting will appear here.",
				tooltipPlacement: "top",
				tooltipTarget: "[data-tour-tooltip-target='action-items-highlight']",
				showSpotlight: false,
				allowInteraction: false,
			},
			{
				tooltipTitle: "Your meeting transcribed",
				tooltipContent: "Find the full meeting transcript here.",
				tooltipPlacement: "bottom",
				tooltipTarget: `[data-tour-tooltip-target='toggle-group-${TRANSCRIPT_TAB.value}']`,
				showSpotlight: false,
				allowInteraction: false,
			},
			{
				tooltipTitle: "Share your Recap",
				tooltipContent:
					"Send your recaps to anyone who wasn't in the meeting or share them to a channel in your library.",
				tooltipPlacement: "bottom",
				tooltipTarget: "[data-tour-tooltip-target='share-notes-button']",
				showSpotlight: false,
			},
		],
		finalStep: () => {
			__globalModalContext?.closeAll();
			commitMutation(environment, {
				mutation: graphql`
					mutation Tours_CompleteWelcomeChecklistTourMutation($taskKey: WelcomeChecklistTaskKey!) {
						completeWelcomeChecklistTour(taskKey: $taskKey) {
							viewer {
								id
								...Checklist_viewer_tasks @relay(mask: false)
							}
						}
					}
				`,
				variables: {
					taskKey: "REVIEW_FIRST_RECAP",
				},
			});
		},
	},
} satisfies { [key in WelcomeChecklistTaskKey]?: Tour };

const OnSightTours = {
	FLOATING_RECORDING_CONTROLS: {
		title: "Floating recording controls",
		description: "Control and automate AI note-taking so Fellow is in every meeting you want it to be.",
		steps: [
			{
				tooltipTitle: "Your recording controls have moved!",
				tooltipContent:
					// eslint-disable-next-line max-len
					"Control and automate AI note-taking so Fellow is in every meeting you want it to be. Set it to auto-record or start recording on demand.",
				tooltipPlacement: "top",
				tooltipTarget: "[data-tour-tooltip-target='floating-recording-controls']",
				showSpotlight: false,
			},
		],
	} satisfies Tour,
};

const EARLY_ACCESS_TOUR = {
	title: "Early access feedback tour",
	description: "Help us improve the new interface by giving us your feedback.",
	steps: [
		{
			tooltipTitle: "Welcome to the new Fellow!",
			tooltipContent: (
				<>
					<p>
						Thanks for opting into early access. You&apos;re among the first to try Fellow&apos;s new interface.
						We&apos;ve started with updates to meeting notes and recaps—more is coming soon.
					</p>
					<br />
					<p>Start exploring and let us know what you think—your feedback will shape what comes next.</p>
				</>
			),
			tooltipPlacement: "right",
			tooltipTarget: "[data-tour-tooltip-target='navbar-account-dropdown']",
			showSpotlight: true,
			prepareApp: async () => {
				// open the account dropdown
				window.dispatchEvent(new ToggleNavbarDropdownEvent(true));
			},
		},
	],
} satisfies Tour;

const Tours: Record<TourKey, Tour> = {
	...WelcomeChecklistTours,
	...OnSightTours,
	EARLY_ACCESS_TOUR,
};

export type WelcomeChecklistTourKey = keyof typeof WelcomeChecklistTours;
export const WelcomeChecklistTourKeys = Object.keys(WelcomeChecklistTours) as Array<WelcomeChecklistTourKey>;

export function isWelcomeChecklistTourKey(key: string): key is WelcomeChecklistTourKey {
	return WelcomeChecklistTourKeys.includes(key as WelcomeChecklistTourKey);
}

export type OnSightTourKey = keyof typeof OnSightTours;
export const OnSightTourKeys = Object.keys(OnSightTours) as Array<OnSightTourKey>;

export function isOnSightTourKey(key: string): key is OnSightTourKey {
	return OnSightTourKeys.includes(key as OnSightTourKey);
}

export type TourKey = WelcomeChecklistTourKey | OnSightTourKey | "EARLY_ACCESS_TOUR";
export const TourKeys = [...WelcomeChecklistTourKeys, ...OnSightTourKeys, "EARLY_ACCESS_TOUR"] as Array<TourKey>;
export default Tours;
